import React, { useState, useEffect, useRef } from 'react'

import { FULFILLED, REJECTED, OFFER, SAVE } from '../../constants/constants'
import { validateEmail } from '../../helpers/validateEmail'
import { setIsSaveProjectToMailModalOpen, setIsSaveProjectOfferModalOpen } from '../../store/ui/uiSlice'
import Heading from '../Heading/Heading'
import Modal from '../Modal/Modal'
import * as commonStyles from '../styles'
import * as styles from './styles'
import { useAppDispatch, useAppSelector } from '../../hooks'

declare global {
  var GLOBAL_colorboxGdpr: () => void
}

const Styled = {
  ...commonStyles,
  ...styles,
}

const ID_FOR_MARKETING_OPT_IN_LINK = 'marketing-opt-in-link'

interface Props {
  onRequestSaveProject: (args: { type: string; customer: any }) => {
    internalSaveUuidv4: string
  }
}

const SaveProject: React.FC<Props> = ({ onRequestSaveProject }) => {
  const dispatch = useAppDispatch()

  const { content } = useAppSelector((state) => state.init)
  const { latestFulfilledId } = useAppSelector((state) => state.internalSaveEvent)
  const { requestStatus_fetchPrices_aggregated, requestStatus_submitForm_aggregated } = useAppSelector(
    (state) => state.requests.requestStatus
  )
  const { isSaveProjectToMailModalOpen, isSaveProjectOfferModalOpen } = useAppSelector((state) => state.ui)

  // true when waiting for savefile from DM iframe
  // **OR** waiting for thunks/submitForm_aggregated promise
  const [isRequestPending, setIsRequestPending] = useState(false)

  const [internalSaveUuidv4, setinternalSaveUuidv4] = useState('')

  const [name, setName] = useState('')
  const [nameErrorMsg, setNameErrorMsg] = useState('')
  const [adress, setAdress] = useState('')
  const [adressErrorMsg, setAdressErrorMsg] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [zipCodeErrorMsg, setZipCodeErrorMsg] = useState('')
  const [city, setCity] = useState('')
  const [cityErrorMsg, setCityErrorMsg] = useState('')
  const [email, setEmail] = useState('')
  const [emailErrorMsg, setEmailErrorMsg] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneErrorMsg, setPhoneErrorMsg] = useState('')
  const [marketingOptIn, setMarketingOptIn] = useState(true)
  const [marketingOptInErrorMsg, setMarketingOptInErrorMsg] = useState('')
  const [saveProjectErrorMsg, setSaveProjectErrorMsg] = useState('')
  const [saveProjectSuccessMsg, setSaveProjectSuccessMsg] = useState('')

  const saveProjectMsgRef = useRef<HTMLSpanElement>(null)

  const onMarketingOptInChange = (): void => {
    if (marketingOptIn) {
      setMarketingOptInErrorMsg((content?.forms?.marketingOptIn?.errorMessage as string) ?? '')
    } else {
      setMarketingOptInErrorMsg('')
    }
    setMarketingOptIn(!marketingOptIn)
    setSaveProjectSuccessMsg('')
  }

  const handleSaveProject = (): void => {
    setIsRequestPending(true)
    setSaveProjectErrorMsg('')
    setSaveProjectSuccessMsg('')
    const { internalSaveUuidv4 } = onRequestSaveProject({
      type: isSaveProjectOfferModalOpen ? OFFER : SAVE,
      customer: {
        name,
        adress,
        zipCode,
        city,
        email,
        phone,
        marketingOptIn,
      },
    })
    setinternalSaveUuidv4(internalSaveUuidv4)
  }

  useEffect(() => {
    if (isRequestPending) {
      if (requestStatus_submitForm_aggregated === FULFILLED) {
        setIsRequestPending(false)

        if (latestFulfilledId === internalSaveUuidv4) {
          setName('')
          setAdress('')
          setZipCode('')
          setCity('')
          setEmail('')
          setPhone('')
          setSaveProjectSuccessMsg(
            isSaveProjectOfferModalOpen
              ? ((content?.forms?.saveProjectOffer?.successMessage as string) ?? '')
              : ((content?.forms?.saveProjectToMail?.successMessage as string) ?? '')
          )
        } else {
          console.error('Unexpected error in SaveProject component; latestFulfilledId !== internalSaveUuidv4')
        }
      }

      if (requestStatus_submitForm_aggregated === REJECTED) {
        setIsRequestPending(false)
        setSaveProjectErrorMsg((content?.forms?.errors?.rejected as string) ?? '')
      }
    }
  }, [requestStatus_submitForm_aggregated])

  useEffect(() => {
    if (saveProjectErrorMsg || saveProjectSuccessMsg) {
      saveProjectMsgRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [saveProjectErrorMsg, saveProjectSuccessMsg])

  return (
    <>
      <Modal
        isOpen={isSaveProjectOfferModalOpen || isSaveProjectToMailModalOpen}
        onRequestClose={() => {
          if (isSaveProjectOfferModalOpen) {
            dispatch(setIsSaveProjectOfferModalOpen(false))
          } else if (isSaveProjectToMailModalOpen) {
            dispatch(setIsSaveProjectToMailModalOpen(false))
          }
        }}
        onAfterClose={() => {
          setSaveProjectSuccessMsg('')
        }}
        contentLabel={content?.modals?.saveProjectToMail?.menu} // only for screenreaders
        testId={`dialog-save-project-${isSaveProjectOfferModalOpen ? 'offer' : 'to-mail'}`}
      >
        {/* Adding classNames to apply Scensum styling */}
        <Styled.ModalSaveProject className="form-group">
          <Heading level={2}>
            {isSaveProjectOfferModalOpen
              ? content?.modals?.saveProjectOffer?.title
              : content?.modals?.saveProjectToMail?.title}
          </Heading>

          {isSaveProjectOfferModalOpen ? (
            <>
              <p>{content?.modals?.saveProjectOffer?.description}</p>
              <p>{content?.modals?.saveProjectOffer?.description_2}</p>
              <p>{content?.modals?.saveProjectOffer?.description_3}</p>
              <p>
                <i
                  style={{
                    fontStyle: 'italic',
                    fontSize: '12px',
                    lineHeight: '1',
                  }}
                >
                  {content?.modals?.saveProjectOffer?.disclaimer}
                </i>
              </p>
            </>
          ) : (
            <p>{content?.modals?.saveProjectToMail?.description}</p>
          )}

          {isSaveProjectOfferModalOpen && (
            <>
              <label htmlFor="save-project-name">{content?.forms?.name?.label}</label>
              <input
                id="save-project-name"
                className="form-control"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                  setNameErrorMsg('')
                  setSaveProjectSuccessMsg('')
                }}
                data-testid="save-project-offer-name-input"
              />
              {nameErrorMsg && <span className="error-message">{nameErrorMsg}</span>}
            </>
          )}

          {isSaveProjectOfferModalOpen && (
            <>
              <label htmlFor="save-project-adress">{content?.forms?.adress?.label}</label>
              <input
                id="save-project-adress"
                className="form-control"
                type="text"
                autoComplete="street-address"
                value={adress}
                onChange={(event) => {
                  setAdress(event.target.value)
                  setAdressErrorMsg('')
                  setSaveProjectSuccessMsg('')
                }}
                data-testid="save-project-offer-adress-input"
              />
              {adressErrorMsg && <span className="error-message">{adressErrorMsg}</span>}
            </>
          )}

          {isSaveProjectOfferModalOpen && (
            <>
              <label htmlFor="save-project-zip-code">{content?.forms?.zipCode?.label}</label>
              <input
                id="save-project-zip-code"
                className="form-control"
                type="text"
                autoComplete="postal-code"
                value={zipCode}
                onChange={(event) => {
                  setZipCode(event.target.value)
                  setZipCodeErrorMsg('')
                  setSaveProjectSuccessMsg('')
                }}
                data-testid="save-project-offer-zip-code-input"
              />
              {zipCodeErrorMsg && <span className="error-message">{zipCodeErrorMsg}</span>}
            </>
          )}

          {isSaveProjectOfferModalOpen && (
            <>
              <label htmlFor="save-project-city">{content?.forms?.city?.label}</label>
              <input
                id="save-project-city"
                className="form-control"
                type="text"
                autoComplete="address-level1"
                value={city}
                onChange={(event) => {
                  setCity(event.target.value)
                  setCityErrorMsg('')
                  setSaveProjectSuccessMsg('')
                }}
                data-testid="save-project-offer-city-input"
              />
              {cityErrorMsg && <span className="error-message">{cityErrorMsg}</span>}
            </>
          )}

          <label htmlFor="save-project-email">{content?.forms?.email?.label}</label>
          <input
            id="save-project-email"
            className="form-control"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              setEmailErrorMsg('')
              setSaveProjectSuccessMsg('')
            }}
            data-testid="save-project-email-input"
          />
          {emailErrorMsg && <span className="error-message">{emailErrorMsg}</span>}

          {isSaveProjectOfferModalOpen && (
            <>
              <label htmlFor="save-project-phone">{content?.forms?.phone?.label}</label>
              <input
                id="save-project-phone"
                className="form-control"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value)
                  setPhoneErrorMsg('')
                  setSaveProjectSuccessMsg('')
                }}
                data-testid="save-project-offer-phone-input"
              />
              {phoneErrorMsg && <span className="error-message">{phoneErrorMsg}</span>}
            </>
          )}

          <input
            id="save-project-marketing-opt-in"
            type="checkbox"
            checked={marketingOptIn}
            onChange={() => {
              // handled by label onClick
            }}
            data-testid="save-project-marketing-opt-in-checkbox"
          />
          <label
            htmlFor="save-project-marketing-opt-in"
            onClick={(event: any) => {
              if (event.target.id === ID_FOR_MARKETING_OPT_IN_LINK) {
                return
              }

              onMarketingOptInChange()
            }}
          >
            {content?.forms?.marketingOptIn?.label}

            <a
              id={ID_FOR_MARKETING_OPT_IN_LINK}
              href={content?.forms?.marketingOptIn?.linkPath}
              onClick={(event) => {
                event.preventDefault()

                if (typeof GLOBAL_colorboxGdpr === 'function') {
                  GLOBAL_colorboxGdpr()
                } else {
                  console.error('function GLOBAL_colorboxGdpr not found')
                }
              }}
            >
              {content?.forms?.marketingOptIn?.linkText}
              <i className="far fa-arrow-up" aria-hidden="true" />
            </a>
          </label>
          {marketingOptInErrorMsg && <span className="error-message">{marketingOptInErrorMsg}</span>}

          <Styled.Button
            className="button-primary-large"
            disabled={requestStatus_fetchPrices_aggregated !== FULFILLED || isRequestPending || !!saveProjectSuccessMsg}
            isLoading={isRequestPending}
            data-testid={`save-project-submit-button-${isSaveProjectOfferModalOpen ? 'offer' : 'to-mail'}`}
            onClick={() => {
              let hasAnyError = false

              if (!email) {
                hasAnyError = true
                setEmailErrorMsg((content?.forms?.email?.errorMessage as string) ?? '')
              } else if (!validateEmail(email)) {
                hasAnyError = true
                setEmailErrorMsg((content?.forms?.email?.errorMessageFormat as string) ?? '')
              }

              if (!marketingOptIn) {
                hasAnyError = true
                setMarketingOptInErrorMsg((content?.forms?.marketingOptIn?.errorMessage as string) ?? '')
              }

              if (isSaveProjectOfferModalOpen) {
                if (!name) {
                  hasAnyError = true
                  setNameErrorMsg((content?.forms?.name?.errorMessage as string) ?? '')
                }
                if (!adress) {
                  hasAnyError = true
                  setAdressErrorMsg((content?.forms?.adress?.errorMessage as string) ?? '')
                }
                if (!zipCode) {
                  hasAnyError = true
                  setZipCodeErrorMsg((content?.forms?.zipCode?.errorMessage as string) ?? '')
                }
                if (!city) {
                  hasAnyError = true
                  setCityErrorMsg((content?.forms?.city?.errorMessage as string) ?? '')
                }
                if (!phone) {
                  hasAnyError = true
                  setPhoneErrorMsg((content?.forms?.phone?.errorMessage as string) ?? '')
                }
              }

              if (hasAnyError) {
                setSaveProjectErrorMsg((content?.forms?.errors?.incomplete as string) ?? '')
                return
              }

              handleSaveProject()
            }}
          >
            {content?.buttons?.submit}
          </Styled.Button>
          {saveProjectErrorMsg && <span className="error-message">{saveProjectErrorMsg}</span>}
          {saveProjectSuccessMsg && (
            <span className="success-message" data-testid="save-project-success-message">
              <i className="far fa-check fa-lg" aria-hidden="true" />
              {saveProjectSuccessMsg}
            </span>
          )}
          <span
            ref={saveProjectMsgRef}
            style={{
              display: saveProjectErrorMsg || saveProjectSuccessMsg ? 'block' : 'none',
            }}
          />
        </Styled.ModalSaveProject>
      </Modal>
    </>
  )
}

export default SaveProject
